import type {
  Catchup,
  CatchupProgram,
  CatchupProgramsWithDates,
} from '@setplex/tria-api'
import type { TFunction } from '~/core/i18n'
import type { AdjustPurchaseableFn } from '~/features/payment/sse-purchases'
import type {
  CatchupFormatted,
  CatchupProgramFormatted,
  CatchupsRedirectParams,
} from '~/shared/interfaces'

import { ScheduleType } from '@setplex/tria-api'
import { isToday } from 'date-fns'
import { t } from '~/core/i18n'
import {
  DATE_FORMATS,
  formatDate,
  formatTimeRangeUnified,
} from '~/shared/helpers/date'
import { getLabelPrice } from '~/shared/tools'

export const formatCatchupsDate = (startOfDayDate: number, t: TFunction) => {
  const formattedDate = formatDate(startOfDayDate, DATE_FORMATS.DD_MMM)

  const date = isToday(startOfDayDate)
    ? t('today')
    : t('date-formatted', { lng: 'date', date: formattedDate })

  return { date, stamp: startOfDayDate }
}

export const formatPaidCatchup = (
  catchup: Catchup,
  adjust: AdjustPurchaseableFn
): CatchupFormatted => {
  const channel = adjust(catchup.channel)

  const { free, purchaseInfo, name, channelNumber } = channel
  return {
    ...catchup,
    channel: {
      ...channel,
      formattedName: `${channelNumber}. ${name}`,
      labelPrice: getLabelPrice({
        free: Boolean(free),
        purchaseInfo,
      }),
    },
  }
}

export const formatProgram = (
  program: CatchupProgram,
  catchup: Catchup
): CatchupProgramFormatted => {
  const timeline = formatTimeRangeUnified(program.startMs, program.endMs)
  const formattedCardText = t('n_a')

  return catchup?.scheduleType === ScheduleType.EPG
    ? {
        ...program,
        cardTitle: program.name,
        cardText: timeline,
      }
    : {
        ...program,
        cardTitle: timeline,
        cardText: formattedCardText,
      }
}

export const formatProgramsForSelectedDate = ({
  programs = [],
  catchup,
}: {
  programs?: CatchupProgram[]
  catchup: Catchup
}): CatchupProgramFormatted[] => {
  return programs
    .map((program) => formatProgram(program, catchup))
    .sort((a, b) => b.startMs - a.startMs)
}

export type CatchupAirTime = {
  startMs?: number
  stopMs?: number
}

const flattenCatchupPrograms = (
  programs: CatchupProgramsWithDates
): CatchupProgram[] =>
  Object.values(programs).flatMap((entry) =>
    Array.isArray(entry) ? entry : []
  )

/**
 * Search Recently Aired cards carry programme start/stop but not a programme
 * id. Match against the catch-up catalogue by air time: exact window, then
 * start, then the programme that contains the card's midpoint.
 */
export function findCatchupProgramIdByAirTime(
  programs: CatchupProgramsWithDates,
  { startMs, stopMs }: CatchupAirTime
): number | null {
  if (startMs == null || !Number.isFinite(startMs)) return null

  const list = flattenCatchupPrograms(programs)
  if (list.length === 0) return null

  const stopOk = stopMs != null && Number.isFinite(stopMs)

  const exactWindow = stopOk
    ? list.find(
        (program) => program.startMs === startMs && program.endMs === stopMs
      )
    : undefined
  if (exactWindow != null) return exactWindow.id

  const exactStart = list.find((program) => program.startMs === startMs)
  if (exactStart != null) return exactStart.id

  if (stopOk) {
    const midpoint = startMs + (stopMs - startMs) / 2
    const containing = list.find(
      (program) => program.startMs <= midpoint && midpoint < program.endMs
    )
    if (containing != null) return containing.id
  }

  return null
}

export const lockedFormat = ({
  allCatchups,
  redirect,
  unlockId,
  defaultStore,
}: {
  allCatchups: CatchupFormatted[]
  redirect: CatchupsRedirectParams
  unlockId: number
  defaultStore: Catchup[]
}): CatchupFormatted[] => {
  const channels = allCatchups.map((catchup, i) => {
    if (catchup.channel.id === redirect.channelId) {
      const locked = catchup.channel.id !== unlockId

      return {
        ...catchup,
        channel: {
          ...catchup.channel,
          locked,
        },
      }
    }
    return {
      ...catchup,
      channel: {
        ...catchup.channel,
        locked: defaultStore[i].channel.locked,
      },
    }
  })
  return channels
}
